<template>
  <div>

    <Teleport to="title">{{ isNew ? $t('titles.create_user') : $t('titles.edit_user_with_username', {username: props.user.username}) }}</Teleport>

    <v-form @submit.prevent="submit" novalidate>

      <template v-if="canManageOrganizations">

        <o-autocomplete
          v-if="isNew"
          :label="$t('titles.organization')"
          v-model="v$.organizationID.$model"
          :validator="v$.organizationID"
          :items="orgOptions"
        />

        <o-text-field
          v-if="!isNew"
          :label="$t('titles.organization')"
          readonly
          :model-value="user.organization ? user.organization.name : '(unknown)'"
        />

      </template>

      <v-row>

        <v-col cols="12" sm="6">

          <o-text-field
            v-if="isNew"
            :label="$t('titles.username')"
            v-model="v$.username.$model"
            :validator="v$.username"
            :hint="$t('misc.username_requirements')"
            persistent-hint
          />

          <o-text-field
            v-if="!isNew"
            :label="$t('titles.username')"
            readonly
            :model-value="formState.username"
          />

        </v-col>

        <v-col cols="12" sm="6">
          <o-select
            :label="$t('titles.role')"
            v-model="v$.role.$model"
            :validator="v$.role"
            :items="roleOptions"
          />
        </v-col>

      </v-row>

      <v-row>

        <v-col cols="12" sm="6">
          <o-text-field
            :label="$t('titles.first_name')"
            required
            v-model="v$.firstName.$model"
            :validator="v$.firstName"
          />
        </v-col>

        <v-col cols="12" sm="6">
          <o-text-field
            :label="$t('titles.last_name')"
            required
            v-model="v$.lastName.$model"
            :validator="v$.lastName"
          />
        </v-col>

      </v-row>

      <v-row>

        <v-col cols="12" sm="6">
          <o-text-field
            :label="$t('titles.email_address')"
            type="email"
            v-model="v$.email.$model"
            :validator="v$.email"
            required
          />
        </v-col>

        <v-col cols="12" sm="6">
          <o-text-field
            :label="$t('titles.phone_number')"
            type="tel"
            v-model="v$.phone.$model"
            :validator="v$.phone"
          />
        </v-col>

      </v-row>

      <o-switch
        :label="$t('misc.live')"
        v-model="formState.live"
      />

      <o-switch
        :label="$t('misc.platform_access')"
        v-model="formState.platformAccess"
      />

      <o-switch
        :label="$t('misc.terminal_access')"
        v-model="formState.terminalAccess"
      />

      <v-btn
        type="submit"
        prepend-icon="$confirm"
        :loading="submitting"
        :disabled="submitting"
      >{{ $t('actions.save') }}</v-btn>

    </v-form>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// models
import Organization from '../../../models/Organization'
import User from '../../../models/User'

// validation
import username from '../../../rules/username'
import useVuelidate from '@vuelidate/core'
import { required, email, minLength } from '../../../utils/i18n-validators'
import { helpers } from '@vuelidate/validators'

// misc
import NotificationService from '../../../services/NotificationService'
import store from '../../../plugins/vuex'
import { UserRoles } from '../../../enums'
import type { Can } from '../../../main'
import type { VSelectItem } from '../../../types/vuetify'

interface Props {
  user: User
}

const props = defineProps<Props>()

const { t } = useI18n()
const router = useRouter()

const $can = inject('$can') as Can
const canManageOrganizations = $can('organizations.manage')

const isNew = props.user.ID === null

/**
 * Form state and rules
 */

const formState = reactive({
  username: props.user.username,
  firstName: props.user.firstName,
  lastName: props.user.lastName,
  email: props.user.email,
  phone: props.user.phone,
  organizationID: props.user.organizationID,
  role: props.user.role,
  live: props.user.live,
  platformAccess: props.user.platformAccess,
  terminalAccess: props.user.terminalAccess,
})

// if we're creating a new user, then set some sensible defaults
if (isNew) {
  formState.organizationID = store.state.auth.user.organizationID
  formState.role = formState.organizationID === 0 ? 'super_admin' : 'admin'
  formState.platformAccess = true
}

const formRules = {
  username: {
    required,
    minLength: minLength(2),
    username: helpers.withMessage(t('validations.fails_username_requirements'), username),
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  email: {
    required,
    email,
  },
  phone: {},
  organizationID: {},
  role: {
    required,
  },
}

const v$ = useVuelidate(formRules, formState)

const orgOptions = computed(() => {
  return Organization.getOptions(true)
})

const roleOptions = computed<VSelectItem[]>(() => {

  const optionValues = []

  if (formState.organizationID === 0) { // Ocra
    optionValues.push('super_admin')
  } else {
    optionValues.push('admin')
    optionValues.push('user')
  }

  return optionValues.map((optionValue) => {
    return {
      value: optionValue,
      title: UserRoles[optionValue] ?? optionValue,
    }
  })

})

// When the Organization is changed, make sure the Role field doesn't get left
// with an invalid value
watch(() => formState.organizationID, (newOrganizationID) => {
  if (newOrganizationID === 0 && formState.role !== 'super_admin') {
    formState.role = 'super_admin'
  } else if (newOrganizationID !== 0 && formState.role === 'super_admin') {
    formState.role = 'user'
  }
})

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate();

  if (!isValid) {
    return;
  }

  submitting.value = true

  const payload = {
    ID: props.user.ID,
    username: formState.username,
    firstName: formState.firstName,
    lastName: formState.lastName,
    email: formState.email,
    phone: formState.phone,
    organizationID: formState.organizationID,
    role: formState.role,
    live: formState.live,
    platformAccess: formState.platformAccess,
    terminalAccess: formState.terminalAccess,
  }

  const savePromise = isNew ? User.saveNew(payload) : User.saveExisting(payload)

  savePromise.then((result) => {
    if (isNew) {
      NotificationService.success(t('alerts.user_created'))
      const userID = result!.response.data.ID
      router.push({
        name: 'userDetail', params: { userID: userID }
      })
    } else {
      NotificationService.success(t('alerts.user_updated'))
    }
  })
  .catch((error) => {
    console.error(error)
    NotificationService.error(error)
  })
  .finally(() => {
    submitting.value = false
  })

}

</script>
